import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  Columns3,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileText,
  Plus as PlusIcon,
  RefreshCw,
  Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PracticeCountPill, PracticeShareRing } from '../components/pf/PfUsageMetrics';
import api from '../services/api';

const labelRighePerPagina = 'Righe per pagina:';

const labelDaA = ({ from, to, count }) => {
  const total = count !== -1 ? count : `più di ${to}`;
  return `${from}-${to} di ${total}`;
};

export default function PfTipiDocumento() {
  const definizioneColonne = useMemo(
    () => [
      { key: 'codice', label: 'Codice', sortable: true, align: 'left' },
      { key: 'nome_visualizzato', label: 'Nome', sortable: true, align: 'left' },
      { key: 'obbligatorio', label: 'Obbligatorio', sortable: true, align: 'left' },
      { key: 'attivo', label: 'Attivo', sortable: true, align: 'left' },
      { key: 'ordinamento', label: 'Ordine', sortable: true, align: 'left' },
      { key: 'totale_pratiche_collegate', label: 'Totale pratiche', sortable: true, align: 'right' },
      { key: 'percentuale_sul_totale', label: '% sul totale', sortable: true, align: 'right' }
    ],
    []
  );

  const [tipiDocumento, setTipiDocumento] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState('');
  const [pagina, setPagina] = useState(0);
  const [righePerPagina, setRighePerPagina] = useState(10);
  const [ordine, setOrdine] = useState('asc');
  const [ordinaPer, setOrdinaPer] = useState('ordinamento');
  const [testoFiltro, setTestoFiltro] = useState('');
  const [ancoraMenuColonne, setAncoraMenuColonne] = useState(null);
  const [colonneVisibili, setColonneVisibili] = useState([
    'nome_visualizzato',
    'obbligatorio',
    'attivo',
    'ordinamento',
    'totale_pratiche_collegate',
    'percentuale_sul_totale'
  ]);

  const [openDialog, setOpenDialog] = useState(false);
  const [rigaCorrente, setRigaCorrente] = useState(null);
  const [formDati, setFormDati] = useState({
    codice: '',
    nome_visualizzato: '',
    obbligatorio: true,
    attivo: true,
    ordinamento: 0
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const caricaTipiDocumento = async () => {
    setCaricamento(true);
    try {
      const response = await api.get('/pf-tipi-documento');
      setTipiDocumento(response.data || []);
      setErrore('');
    } catch (err) {
      console.error('Error fetching pf tipi documento:', err);
      setErrore('Errore nel caricamento dei tipi documento');
      toast.error('Errore nel caricamento dei tipi documento');
    } finally {
      setCaricamento(false);
    }
  };

  useEffect(() => {
    caricaTipiDocumento();
  }, []);

  const colonneMostrate = useMemo(() => definizioneColonne.filter((c) => colonneVisibili.includes(c.key)), [definizioneColonne, colonneVisibili]);

  const righeFiltrate = useMemo(() => {
    const q = testoFiltro.trim().toLowerCase();
    if (!q) return tipiDocumento;
    return tipiDocumento.filter((r) => {
      const haystack = [r.codice, r.nome_visualizzato].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [tipiDocumento, testoFiltro]);

  const righeOrdinate = useMemo(() => {
    const stabilized = righeFiltrate.map((el, index) => [el, index]);
    const isAsc = ordine === 'asc';
    stabilized.sort((a, b) => {
      const rowA = a[0];
      const rowB = b[0];
      const valA = rowA?.[ordinaPer];
      const valB = rowB?.[ordinaPer];

      if (
        ordinaPer === 'attivo' ||
        ordinaPer === 'obbligatorio' ||
        ordinaPer === 'id' ||
        ordinaPer === 'ordinamento' ||
        ordinaPer === 'totale_pratiche_collegate' ||
        ordinaPer === 'percentuale_sul_totale'
      ) {
        const numA = Number(valA || 0);
        const numB = Number(valB || 0);
        return isAsc ? numA - numB : numB - numA;
      }

      const strA = (valA || '').toString().toLowerCase();
      const strB = (valB || '').toString().toLowerCase();
      if (strA < strB) return isAsc ? -1 : 1;
      if (strA > strB) return isAsc ? 1 : -1;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  }, [righeFiltrate, ordine, ordinaPer]);

  const handleRequestSort = (property) => {
    const isAsc = ordinaPer === property && ordine === 'asc';
    setOrdine(isAsc ? 'desc' : 'asc');
    setOrdinaPer(property);
  };

  const handleChangePage = (_event, newPage) => {
    setPagina(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRighePerPagina(parseInt(event.target.value, 10));
    setPagina(0);
  };

  const handleFilterChange = (e) => {
    setTestoFiltro(e.target.value);
    setPagina(0);
  };

  const handleToggleColumn = (key) => {
    setColonneVisibili((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  const handleOpenDialog = (row = null) => {
    if (row) {
      setRigaCorrente(row);
      setFormDati({
        codice: row.codice || '',
        nome_visualizzato: row.nome_visualizzato || '',
        obbligatorio: Boolean(row.obbligatorio),
        attivo: Boolean(row.attivo),
        ordinamento: Number(row.ordinamento || 0)
      });
    } else {
      setRigaCorrente(null);
      setFormDati({
        codice: '',
        nome_visualizzato: '',
        obbligatorio: true,
        attivo: true,
        ordinamento: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setRigaCorrente(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormDati((prev) => ({
      ...prev,
      [name]: name === 'ordinamento' ? Number(value) : value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormDati((prev) => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async () => {
    try {
      if (rigaCorrente) {
        await api.put(`/pf-tipi-documento/${rigaCorrente.id}`, formDati);
        toast.success('Tipo documento aggiornato con successo');
      } else {
        await api.post('/pf-tipi-documento', formDati);
        toast.success('Tipo documento creato con successo');
      }
      handleCloseDialog();
      caricaTipiDocumento();
    } catch (err) {
      console.error('Error saving pf tipo documento:', err);
      toast.error(err.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/pf-tipi-documento/${deleteTarget.id}`);
      toast.success('Tipo documento eliminato con successo');
      closeDeleteDialog();
      caricaTipiDocumento();
    } catch (err) {
      console.error('Error deleting pf tipo documento:', err);
      toast.error("Errore durante l'eliminazione");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <FileText className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <Typography variant="h4" className="font-bold text-gray-800 dark:text-gray-100">
              Parametri — Tipi Documento
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Anagrafica dei documenti richiesti nelle Pratiche Finanziarie
            </Typography>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={caricaTipiDocumento} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuovo Tipo Documento
          </Button>
        </div>
      </div>

      {errore && (
        <Alert severity="error" className="mb-4">
          {errore}
        </Alert>
      )}

      <Menu anchorEl={ancoraMenuColonne} open={Boolean(ancoraMenuColonne)} onClose={() => setAncoraMenuColonne(null)}>
        {definizioneColonne.map((column) => {
          const checked = colonneVisibili.includes(column.key);
          const disableHide = checked && colonneVisibili.length === 1;
          return (
            <MenuItem key={column.key} dense onClick={() => handleToggleColumn(column.key)} disabled={disableHide}>
              <Checkbox size="small" checked={checked} />
              <ListItemText primary={column.label} />
            </MenuItem>
          );
        })}
      </Menu>

      <Card className="shadow-sm border-none overflow-hidden rounded-xl">
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={testoFiltro}
            onChange={handleFilterChange}
            placeholder="Cerca per codice o nome..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              )
            }}
            sx={{ maxWidth: 520 }}
          />
          <Tooltip title="Colonne">
            <IconButton onClick={(e) => setAncoraMenuColonne(e.currentTarget)}>
              <Columns3 size={18} />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                {colonneMostrate.map((column) => (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    sx={column.key === 'totale_pratiche_collegate' ? { minWidth: 104, whiteSpace: 'nowrap' } : column.key === 'percentuale_sul_totale' ? { minWidth: 88, whiteSpace: 'nowrap' } : undefined}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={ordinaPer === column.key}
                        direction={ordinaPer === column.key ? ordine : 'asc'}
                        onClick={() => handleRequestSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {caricamento ? (
                <TableRow>
                  <TableCell colSpan={colonneMostrate.length + 1} align="center" className="py-8">
                    <RefreshCw className="mx-auto h-6 w-6 animate-spin text-gray-400" />
                  </TableCell>
                </TableRow>
              ) : righeOrdinate.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colonneMostrate.length + 1} align="center" className="py-8 text-gray-500">
                    Nessun tipo documento trovato
                  </TableCell>
                </TableRow>
              ) : (
                righeOrdinate
                  .slice(pagina * righePerPagina, pagina * righePerPagina + righePerPagina)
                  .map((row) => (
                    <TableRow key={row.id} hover>
                      {colonneMostrate.map((column) => (
                        <TableCell
                          key={`${row.id}-${column.key}`}
                          align={column.align}
                          className={column.key === 'nome_visualizzato' ? 'font-medium' : undefined}
                          sx={column.key === 'totale_pratiche_collegate' ? { minWidth: 104 } : column.key === 'percentuale_sul_totale' ? { minWidth: 88 } : undefined}
                        >
                          {column.key === 'attivo' ? (
                            <Checkbox checked={Boolean(row.attivo)} disabled />
                          ) : column.key === 'obbligatorio' ? (
                            <Checkbox checked={Boolean(row.obbligatorio)} disabled />
                          ) : column.key === 'totale_pratiche_collegate' ? (
                            <PracticeCountPill value={row.totale_pratiche_collegate} />
                          ) : column.key === 'percentuale_sul_totale' ? (
                            <PracticeShareRing value={row.percentuale_sul_totale} />
                          ) : (
                            row[column.key] ?? '-'
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => handleOpenDialog(row)} color="primary">
                            <EditIcon size={18} />
                          </IconButton>
                          <IconButton size="small" onClick={() => openDeleteDialog(row)} color="error">
                            <DeleteIcon size={18} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={righeOrdinate.length}
          rowsPerPage={righePerPagina}
          page={pagina}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={labelRighePerPagina}
          labelDisplayedRows={labelDaA}
        />
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{rigaCorrente ? 'Modifica Tipo Documento' : 'Nuovo Tipo Documento'}</DialogTitle>
        <DialogContent dividers className="space-y-4">
          <TextField
            autoFocus
            margin="dense"
            name="codice"
            label="Codice (univoco)"
            type="text"
            fullWidth
            variant="outlined"
            value={formDati.codice}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            name="nome_visualizzato"
            label="Nome visualizzato"
            type="text"
            fullWidth
            variant="outlined"
            value={formDati.nome_visualizzato}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            name="ordinamento"
            label="Ordine di visualizzazione"
            type="number"
            fullWidth
            variant="outlined"
            value={formDati.ordinamento}
            onChange={handleChange}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox name="obbligatorio" checked={Boolean(formDati.obbligatorio)} onChange={handleCheckboxChange} />
              <Typography>Obbligatorio</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox name="attivo" checked={Boolean(formDati.attivo)} onChange={handleCheckboxChange} />
              <Typography>Attivo</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={handleCloseDialog}>
            Annulla
          </Button>
          <Button onClick={handleSubmit}>Salva</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Vuoi eliminare il tipo documento <strong>{deleteTarget?.nome_visualizzato || deleteTarget?.codice || ''}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
